/* import __COLOCATED_TEMPLATE__ from './signup-credentials-form.hbs'; */
import Component from '@glimmer/component';
import { action, getProperties } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { handleFormErrorOrModal } from 'sh-www/utils/handles-errors';
import { object, string } from 'yup';
import { validateSchema } from 'sh-www/utils/validators';
import Validator from 'validator';
import lottie from 'lottie-web';
import clsx from 'clsx';
import { inject as service } from '@ember/service';

const SCHEMA = object().shape({
  'fullName': string()
    .required('Please enter both a first and last name')
    .test(
      'full-name-parts-requirement',
      'Please enter both a first and last name',
      function(value) {
        const nameParts = value.trim().split(/\s+/)
        if (nameParts.length < 2) return false;
        return true;
      }
    )
    .test(
      'full-name-character-requirement',
      'Your name contains invalid characters.',
      function(value) {
        if (/[^A-Za-zÀ-ÖØ-öø-ÿ-_." "]+/.test(value)) return false;
        return true;
      }
    ),
  'email': string()
    .required('Please enter a valid email')
    .test(
      'email-requirement',
      'Please enter a valid email',
      function(value) {
        return Validator.isEmail(value);
      }
    ),
  'password': string()
    .required()
    .test(
      'password-requirements',
      'Minimum 8 characters, at least 1 uppercase, 1 lowercase, 1 symbol, and 1 number (0-9) required.',
      function(value) {
        if (!value) return false;
        if (value.length < 8) return false;
        if (value.length >= 14) return true; // Drop the special requirements for passwords >= 14 characters
        if (!/[0-9]/.test(value)) return false;
        if (!/[!"\\#$%&'()*+,-.\/:;<=>?@[\]^_`{|}~]/.test(value)) return false;
        if (!/[a-z]/.test(value)) return false;
        if (!/[A-Z]/.test(value)) return false;
        return true;
      }
    ),
});

const WAVE_ANIMATION = '/ember/assets/lottie/wave.json';
const FORM_INPUTS = ['fullName', 'email', 'password'];

const FORM_CLASS_NAME = 'GLM-freelancer-signup-form';

class SignupCredentialsForm extends Component {
  @service session;

  @tracked waveAnimation = null;
  @tracked fullName = this.args.fullName;
  @tracked email = this.args.email;
  @tracked password = '';
  @tracked emailEntered = Boolean(this.args.email);
  @tracked showPasswordIsValid = false;
  @tracked errors = {};

  get classNames() {
    return clsx(
      this.args.class,
    );
  }

  get disabled() {
    return this.submitTask.isRunning || this.googleSSOCallback.isRunning;
  }

  get baseClassName() {
    return FORM_CLASS_NAME;
  }

  get submitDisabled() {
    return this.submitTask.isRunning
      || this.googleSSOCallback.isRunning
      || Object.values(this.errors).some(error => !!error)
      || !this.fullName
      || !this.email
      || !this.password;
  }

  get isPasswordHidden() {
    return !this.emailEntered;
  }

  @action
  onBlurFullName() {
    try {
      SCHEMA.validateSyncAt('fullName', this);
      this.waveAnimation.play();
    } catch (error) {
      //Do nothing
    }
  }

  @action
  handleInput(field, value) {
    this[field] = value;

    if (this.errors[field]) {
      this.errors = { ...this.errors, [field]: undefined };
    }

    if (field === 'email' && !this.emailEntered) {
      try {
        SCHEMA.validateSyncAt('email', this);
        this.emailEntered = true;
      } catch (error) {
        // Do nothing
      }
    }

    if (field === 'password') {
      try {
        SCHEMA.validateSyncAt('password', this);
        this.showPasswordIsValid = true;
      } catch(error) {
        this.showPasswordIsValid = false;
      }
    }
  }

  @action
  validateForm(e) {
    e.preventDefault();
    const [_, validationErrors = {}] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);
    this.errors = validationErrors;

    if (Object.values(this.errors).every(error => !error)) {
      this.submitTask.perform(this.fullName, this.email, this.password);
    }
  }

  @dropTask
  *googleSSOCallback({ credential }) {
    this.errors = {};

    yield this.args.onGoogleSSO(credential);
  }

  @dropTask
  *submitTask(full_name, email, password) {
    try {
      yield this.args.onsubmit?.({ full_name, email, password });
    } catch(e) {
      handleFormErrorOrModal(
        this,
        e,
        FORM_INPUTS,
        false,
        'There was an error creating your account. Please refresh and try again.',
        'signUpFail -- error creating user.' + JSON.stringify(e)
      );
    }
  }

  @action
  loadAnimation(elementId) {
    this.waveAnimation = lottie.loadAnimation({
      container: document.getElementById(elementId),
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: WAVE_ANIMATION,
      rendererSettings: {
        progressiveLoad: true,
      },
    });
  }
}

export default SignupCredentialsForm;
